import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, PawPrint } from 'lucide-react';
import { motion } from 'framer-motion';
import { Header } from '../components/layout/Header';

export function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const cleanEmail = email.trim().toLowerCase();

        if (!cleanEmail || !password) {
            setError('Ingresá email y contraseña.');
            return;
        }

        setLoading(true);
        try {
            const result = await login(cleanEmail, password);

            if (result.success) {
                // Redirección igual que tu App original
                if (Number(result.user.root) === 1) {
                    navigate('/admin', { replace: true });
                } else {
                    navigate(`/consultas/${result.user.id}`, { replace: true });
                }
                return;
            }

            setError(result.error || 'Credenciales inválidas');
        } catch (err) {
            console.error('Login error:', err);
            const status = err.response?.status;
            const apiError = err.response?.data?.error;

            if (status === 401) {
                setError('Email o contraseña incorrectos.');
            } else if (apiError) {
                setError(apiError);
            } else {
                setError('Error de conexión. Verificá tu internet o intentá nuevamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (<>
        <Header />
        <main className="min-h-screen bg-sp-light flex items-center justify-center px-4 xs:px-5 py-8">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-[400px]"
            >
                <div className="bg-white rounded-3xl p-6 xs:p-8 shadow-sp">
                    {/* Header */}
                    <div className="text-center mb-6 xs:mb-8">
                        <div className="w-14 h-14 xs:w-16 xs:h-16 bg-gradient-to-br from-sp-primary to-sp-accent rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <PawPrint className="text-white w-7 h-7 xs:w-8 xs:h-8" />
                        </div>
                        <h1 className="text-[1.4rem] xs:text-[1.6rem] font-black text-sp-text mb-1">
                            Bienvenido a SmartPet
                        </h1>
                        <p className="text-sp-muted text-[0.85rem] xs:text-[0.9rem] font-semibold">
                            Ingresá para gestionar tu mascota
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="bg-red-50 border border-red-200 text-red-600 text-[0.85rem] font-semibold px-4 py-3 rounded-xl mb-4 flex items-center gap-2"
                        >
                            <span>⚠️</span> {error}
                        </motion.div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                        <div>
                            <label htmlFor="email" className="block text-[0.8rem] xs:text-[0.85rem] font-extrabold text-sp-text mb-1.5">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sp-primary/50 w-4 h-4 xs:w-5 xs:h-5" />
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tumail@ejemplo.com"
                                    autoComplete="username"
                                    disabled={loading}
                                    className="w-full h-11 xs:h-12 pl-10 xs:pl-11 pr-4 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.9rem] xs:text-base font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all disabled:opacity-50"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-[0.8rem] xs:text-[0.85rem] font-extrabold text-sp-text mb-1.5">
                                Contraseña
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sp-primary/50 w-4 h-4 xs:w-5 xs:h-5" />
                                <input
                                    type={showPass ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    disabled={loading}
                                    className="w-full h-11 xs:h-12 pl-10 xs:pl-11 pr-12 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.9rem] xs:text-base font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all disabled:opacity-50"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPass((p) => !p)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sp-primary/60 hover:text-sp-primary transition-colors"
                                    aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPass ? <EyeOff className="w-4 h-4 xs:w-5 xs:h-5" /> : <Eye className="w-4 h-4 xs:w-5 xs:h-5" />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !email.trim() || !password}
                            className="w-full h-11 xs:h-12 bg-sp-primary text-white rounded-5xl font-extrabold text-[0.95rem] xs:text-base shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <span className="inline-flex items-center justify-center gap-2">
                                    <span className="w-4 h-4 xs:w-5 xs:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Ingresando...
                                </span>
                            ) : (
                                'Entrar'
                            )}
                        </button>
                    </form>

                    <div className="mt-5 xs:mt-6 space-y-2.5 text-center">
                        <p className="text-[0.8rem] xs:text-[0.85rem] text-sp-body font-semibold">
                            ¿No tenés cuenta?{' '}
                            <Link to="/register" className="text-sp-primary font-extrabold hover:underline underline-offset-2">
                                Registrate acá
                            </Link>
                        </p>
                        <p className="text-[0.8rem] xs:text-[0.85rem] text-sp-body font-semibold">
                            <Link to="/recuperar" className="text-sp-primary font-extrabold hover:underline underline-offset-2">
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </p>
                    </div>
                </div>

                <div className="text-center mt-5 xs:mt-6">
                    <Link to="/" className="inline-flex items-center gap-1.5 text-[0.8rem] xs:text-[0.85rem] font-bold text-sp-muted hover:text-sp-primary transition-colors">
                        ← Volver al inicio
                    </Link>
                </div>
            </motion.div>
        </main>
    </>
    );
}